import React, { useState, useEffect } from 'react'
import GradientButton from '../components/GradientButton'
import FormField from '../components/FormField'
import IconButton from '../components/IconButton'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import type { IconName } from '../components/Icon'
import { useNavigate } from 'react-router-dom'
import { readCurrentFarm, updateCurrentFarm } from '../utils/farmWizard'

const CULTURE_OPTIONS: Array<{
  value: string
  label: string
  description: string
  icon: IconName
}> = [
  { 
    value: 'Fish', 
    label: 'Fish Culture', 
    description: 'Finfish aquaculture including salmon, tilapia, trout, and other fish species',
    icon: 'biomas'
  },
  { 
    value: 'Crustacean', 
    label: 'Crustacean Culture', 
    description: 'Shrimp, crab, lobster, and other crustacean farming',
    icon: 'biomas'
  },
  { 
    value: 'Mollusk', 
    label: 'Mollusk Culture', 
    description: 'Oyster, mussel, clam, and other shellfish farming',
    icon: 'biomas'
  },
  { 
    value: 'Live feed', 
    label: 'Live Feed Production', 
    description: 'Algae, zooplankton, and other live feed organism cultivation',
    icon: 'feed'
  },
  { 
    value: 'Other', 
    label: 'Other Culture Type', 
    description: 'Custom or specialized aquaculture approach',
    icon: 'settings'
  }
]

export default function TypeCulture(){
  const [selected, setSelected] = useState('')
  const [other, setOther] = useState('')
  const [currentFarm, setCurrentFarm] = useState<any>(null)
  const navigate = useNavigate()
  const principal = useStoredPrincipal()
  const canManageFarms = !principal || principal.role === 'editor' || principal.role === 'admin'

  useEffect(()=>{
    const farm = readCurrentFarm()
    setCurrentFarm(farm)
    if (farm?.typeCulture) {
      setSelected(farm.typeCulture)
    }
  },[])

  function persist(){
    const next = updateCurrentFarm((prev) => (
      prev
        ? ({ ...prev, typeCulture: selected === 'Other' ? other : selected } as any)
        : ({ typeCulture: selected === 'Other' ? other : selected } as any)
    ))
    setCurrentFarm(next)
  }

  function onNext(){
    if(!selected) return alert('Please select a culture type')
    if(selected === 'Other' && !other.trim()) return alert('Please describe your culture type')
    persist()
    navigate('/species')
  }

  const containerStyle: React.CSSProperties = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: 'Inter, system-ui, sans-serif'
  }

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px'
  }

  const titleStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }

  const subtitleStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#6b7280',
    lineHeight: '1.6'
  }

  const progressStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
    padding: '12px 16px',
    background: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd'
  }

  const progressTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#0369a1',
    fontWeight: '500'
  }

  const formCardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    padding: '32px'
  }

  const optionsGridStyle: React.CSSProperties = {
    display: 'grid',
    gap: '12px',
    marginBottom: '24px'
  }

  const optionCardStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '16px',
    border: `2px solid ${isSelected ? '#08B8D3' : '#e5e7eb'}`,
    borderRadius: '12px',
    background: isSelected ? '#f0f9ff' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  })

  const optionIconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    color: '#08B8D3',
    flexShrink: 0,
    marginTop: '2px'
  }

  const optionContentStyle: React.CSSProperties = {
    flex: 1
  }

  const optionTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px'
  }

  const optionDescStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5'
  }

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '32px'
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>
          🐟 Culture Type Selection
        </h2>
        <p style={subtitleStyle}>
          Choose the primary aquaculture approach for <strong>{currentFarm?.name || 'your farm'}</strong>. 
          This will help us customize the species options and management tools for your operation.
        </p>
      </div>

      {/* Progress Indicator */}
      <div style={progressStyle}>
        <span style={{ fontSize: '16px' }}>🔄</span>
        <span style={progressTextStyle}>Step 2 of 5: Culture Type</span>
      </div>

      {/* Form Card */}
      <div style={formCardStyle}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#374151', 
          marginBottom: '16px' 
        }}>
          Select Your Primary Culture Type
        </h3>

        <div style={optionsGridStyle}>
          {CULTURE_OPTIONS.map((option) => (
            <div
              key={option.value}
              style={optionCardStyle(selected === option.value)}
              onClick={() => setSelected(option.value)}
              onMouseEnter={(event) => {
                if (selected !== option.value) {
                  event.currentTarget.style.borderColor = '#bae6fd'
                  event.currentTarget.style.background = '#f8fafc'
                }
              }}
              onMouseLeave={(event) => {
                if (selected !== option.value) {
                  event.currentTarget.style.borderColor = '#e5e7eb'
                  event.currentTarget.style.background = '#ffffff'
                }
              }}
            >
              <IconButton
                icon={option.icon}
                variant="ghost"
                size="sm"
                style={optionIconStyle}
                aria-label={`${option.label} icon`}
              />
              <div style={optionContentStyle}>
                <h4 style={optionTitleStyle}>{option.label}</h4>
                <p style={optionDescStyle}>{option.description}</p>
              </div>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: `2px solid ${selected === option.value ? '#08B8D3' : '#d1d5db'}`,
                  background: selected === option.value ? '#08B8D3' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '2px'
                }}
              >
                {selected === option.value && (
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#ffffff'
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Culture Type Input */}
        {selected === 'Other' && (
          <div style={{ marginTop: '16px' }}>
            <FormField
              id="custom-culture"
              label="Custom Culture Type"
              value={other}
              onChange={(value) => setOther(String(value))}
              placeholder="Describe your aquaculture approach..."
              icon="settings"
              required
              hint="Please provide details about your specialized culture method"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div style={buttonGroupStyle}>
          <GradientButton 
            variant="primary"
            size="lg"
            fullWidth
            onClick={onNext}
            disabled={!selected || (selected === 'Other' && !other.trim())}
            icon="biomas"
          >
            Next: Select Species →
          </GradientButton>
        </div>

        <div style={buttonGroupStyle}>
          <GradientButton 
            variant="secondary"
            size="md"
            onClick={() => {
              if (!canManageFarms) return
              navigate('/new-farm')
            }}
            disabled={!canManageFarms}
            icon="home"
          >
            ← Back to Farm Setup
          </GradientButton>
          
          <div style={{ marginLeft: 'auto' }}>
            <IconButton
              icon="chat"
              variant="ghost"
              size="md"
              onClick={() => alert('Help: Choose the type of aquatic organisms you plan to cultivate. This affects available species and management options.')}
            />
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginTop: '32px'
      }}>
        <div style={{
          padding: '16px',
          background: '#f0fdf4',
          borderRadius: '12px',
          border: '1px solid #bbf7d0'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
            💡 Why Culture Type Matters
          </h4>
          <p style={{ fontSize: '13px', color: '#166534', lineHeight: '1.5' }}>
            Your culture type determines water quality parameters, feeding schedules, and monitoring requirements specific to your organisms.
          </p>
        </div>

        <div style={{
          padding: '16px',
          background: '#fef3c7',
          borderRadius: '12px',
          border: '1px solid #fed7aa'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
            🔄 Can Change Later
          </h4>
          <p style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
            You can modify your culture type in farm settings if your operation evolves or expands to new species.
          </p>
        </div>
      </div>
    </div>
  )
}
